import React from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { PrismicLink } from '@prismicio/react'

// Components
import Flag from '../flag'
import ImageGallery from '../imageGallery'
import Map from '../map'

const CompanyLink = ({ slice }) => {
  return (
    <section className='component-company_link mx-auto max-w-screen-xl p-4'>
      <ul>
        {slice.items.map((company, index) => {
          if (!company.link_to_external_companies.document) return null
          let companyData = company.link_to_external_companies.document.data

          return (
            <li className='mb-5 bg-white p-8 md:rounded-lg md:shadow' key={`company-${index}`}>
              <div className='md:flex'>
                <div className='flex-1'>
                  <div className='flex items-center text-sm uppercase tracking-wider text-gray-500'>
                    {companyData.location_country && (
                      <>
                        <Flag lang={companyData.location_country.replace(' ', '')} hideLabel={true} className='mr-1' />
                        <span>
                          {companyData.location_locality}, {companyData.location_country}
                        </span>
                      </>
                    )}
                  </div>
                  <h3 className='my-2 text-2xl font-medium text-primary'>{companyData.name.text}</h3>
                  <p className='mb-4'>{companyData.strapline}</p>
                </div>
                {companyData.logo.gatsbyImageData && (
                  <div>
                    <GatsbyImage className='object-fit' image={companyData.logo.gatsbyImageData} alt={companyData.logo.text || ''} />
                  </div>
                )}
              </div>
              {companyData.body.map((companyBodyData, index) => {
                return (
                  <React.Fragment key={`companyBodyData-${index}`}>
                    {companyBodyData.slice_type === 'text' && (
                      <details className='mt-1 transform overflow-hidden border-b transition-all'>
                        <summary className='m-0 flex w-full cursor-pointer py-4 px-1 text-left text-lg text-blue-900 outline-none hover:text-primaryHover'>
                          <div className='flex-1'>{companyBodyData.primary.section_title}</div>
                          <svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 0 24 24' width='24' fill='currentColor'>
                            <path d='M0 0h24v24H0V0z' fill='none' />
                            <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' />
                          </svg>
                        </summary>
                        <div className='mx-auto mb-4 max-w-screen-xl p-4'>{companyBodyData.primary.text.text}</div>
                      </details>
                    )}
                    {companyBodyData.slice_type === 'image_gallery' && (
                      <details className='mt-1 transform overflow-hidden border-b transition-all'>
                        <summary className='m-0 flex w-full cursor-pointer py-4 px-1 text-left text-lg text-blue-900 outline-none hover:text-primaryHover'>
                          <div className='flex-1'>{companyBodyData.primary.gallery_name.text}</div>
                          <svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 0 24 24' width='24' fill='currentColor'>
                            <path d='M0 0h24v24H0V0z' fill='none' />
                            <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' />
                          </svg>
                        </summary>
                        <div className='relative mx-auto max-w-screen-md py-4 md:px-4'>
                          <ImageGallery autoplay loop delayLength={5000} arrows slice={companyBodyData} />
                        </div>
                      </details>
                    )}
                    {companyBodyData.slice_type === 'locations' && (
                      <details className='my-1 transform overflow-hidden border-b transition-all'>
                        <summary className='m-0 flex w-full cursor-pointer py-4 px-1 text-left text-lg text-blue-900 outline-none hover:text-primaryHover'>
                          <div className='flex-1'>{companyBodyData.primary.section_title}</div>
                          <svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 0 24 24' width='24' fill='currentColor'>
                            <path d='M0 0h24v24H0V0z' fill='none' />
                            <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' />
                          </svg>
                        </summary>
                        <div className='mx-auto mb-4 max-w-screen-xl grid-cols-4 gap-6 p-4 md:grid'>
                          <div className='mb-4 md:col-span-2'>
                            <dl className='grid h-auto grid-cols-3 gap-3'>
                              {companyBodyData.primary.contact && (
                                <>
                                  <dt className='text-gray-500'>Contact</dt>
                                  <dd className='col-span-2'>{companyBodyData.primary.contact}</dd>
                                </>
                              )}
                              {companyBodyData.primary.phone && (
                                <>
                                  <dt className='text-gray-500'>Tel</dt>
                                  <dd className='col-span-2'>{companyBodyData.primary.phone}</dd>
                                </>
                              )}
                              {companyBodyData.primary.fax && (
                                <>
                                  <dt className='text-gray-500'>Fax</dt>
                                  <dd className='col-span-2'>{companyBodyData.primary.fax}</dd>
                                </>
                              )}
                              {companyBodyData.primary.email && (
                                <>
                                  <dt className='text-gray-500'>Email</dt>
                                  <dd className='col-span-2'>
                                    <PrismicLink
                                      href={`mailto:${companyBodyData.primary.email}`}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='transform cursor-pointer font-normal text-blue-800 transition duration-150 ease-linear hover:text-secondary'>
                                      {companyBodyData.primary.email}
                                    </PrismicLink>
                                  </dd>
                                </>
                              )}
                              {companyData.website && (
                                <>
                                  <dt className='text-gray-500'>Website</dt>
                                  <dd className='col-span-2 truncate'>
                                    <a
                                      href={companyData.website}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='transform cursor-pointer font-normal text-blue-800 transition duration-150 ease-linear hover:text-secondary'>
                                      {companyData.website}
                                    </a>
                                  </dd>
                                </>
                              )}
                              {companyBodyData.primary.address && (
                                <>
                                  <dt className='text-gray-500'>Address</dt>
                                  <dd className='col-span-2'>{companyBodyData.primary.address.text}</dd>
                                </>
                              )}
                            </dl>
                          </div>
                          {companyData.map?.latitude !== 0 && companyData.map?.latitude !== null && companyData.map?.longitude !== 0 && companyData.map?.longitude !== null && (
                            <div className='md:col-span-2'>
                              <Map latitude={companyData.map?.latitude} longitude={companyData.map?.longitude} />
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </React.Fragment>
                )
              })}
              {companyData.website && (
                <div className='mt-6'>
                  <a className='rounded-md border border-orange-500 px-2 py-1 text-sm text-secondary hover:bg-orange-500 hover:text-white' href={companyData.website}>
                    <svg xmlns='http://www.w3.org/2000/svg' className='-mt-1 inline h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                      <path d='M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z' />
                      <path d='M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z' />
                    </svg>
                    Visit site
                  </a>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default CompanyLink
